import React, { createContext, useContext, useState } from 'react';

const MUTE_STORAGE_KEY = 'big2.soundMuted';

function getInitialMuted(): boolean {
  if (typeof localStorage !== 'undefined' && typeof localStorage?.getItem === 'function') {
    return localStorage.getItem(MUTE_STORAGE_KEY) === 'true';
  }
  return false;
}

let sharedCtx: AudioContext | null = null;
let noiseBuffer: AudioBuffer | null = null;

export function isMutedGlobal(): boolean {
  if (typeof localStorage !== 'undefined' && typeof localStorage?.getItem === 'function') {
    return localStorage.getItem(MUTE_STORAGE_KEY) === 'true';
  }
  return false;
}

export function getAudioContext(): AudioContext | null {
  if (typeof window === 'undefined') return null;
  const AudioCtx = window.AudioContext || (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
  if (!AudioCtx) return null;
  if (!sharedCtx) {
    try {
      sharedCtx = new AudioCtx();
    } catch {
      return null;
    }
  }
  if (sharedCtx.state === 'suspended') {
    sharedCtx.resume().catch(() => {});
  }
  return sharedCtx;
}

function getNoiseBuffer(ctx: AudioContext): AudioBuffer {
  if (noiseBuffer && noiseBuffer.sampleRate === ctx.sampleRate) {
    return noiseBuffer;
  }
  const length = Math.floor(ctx.sampleRate * 0.25);
  const buffer = ctx.createBuffer(1, length, ctx.sampleRate);
  const data = buffer.getChannelData(0);
  for (let i = 0; i < length; i++) {
    data[i] = Math.random() * 2 - 1;
  }
  noiseBuffer = buffer;
  return buffer;
}

/** Single crisp card snap/flick helper scheduled at `startTime`. */
function scheduleCardSnap(ctx: AudioContext, startTime: number, pitchMultiplier = 1, volume = 0.28) {
  const noise = ctx.createBufferSource();
  noise.buffer = getNoiseBuffer(ctx);

  const filter = ctx.createBiquadFilter();
  filter.type = 'bandpass';
  filter.frequency.setValueAtTime(1600 * pitchMultiplier, startTime);
  filter.Q.setValueAtTime(1.8, startTime);

  const noiseGain = ctx.createGain();
  noiseGain.gain.setValueAtTime(0.001, startTime);
  noiseGain.gain.linearRampToValueAtTime(volume, startTime + 0.004);
  noiseGain.gain.exponentialRampToValueAtTime(0.001, startTime + 0.045);

  noise.connect(filter);
  filter.connect(noiseGain);
  noiseGain.connect(ctx.destination);

  noise.start(startTime);
  noise.stop(startTime + 0.05);

  // Subtle low-mid felt thud
  const osc = ctx.createOscillator();
  const thudGain = ctx.createGain();
  osc.type = 'sine';
  osc.frequency.setValueAtTime(155 * pitchMultiplier, startTime);
  osc.frequency.exponentialRampToValueAtTime(52, startTime + 0.055);

  thudGain.gain.setValueAtTime(volume * 0.65, startTime);
  thudGain.gain.exponentialRampToValueAtTime(0.001, startTime + 0.06);

  osc.connect(thudGain);
  thudGain.connect(ctx.destination);

  osc.start(startTime);
  osc.stop(startTime + 0.065);
}

/** 1. Card shuffling & dealing cascade sound when a round starts. */
export function playShuffleSound() {
  if (isMutedGlobal()) return;
  const ctx = getAudioContext();
  if (!ctx) return;

  const now = ctx.currentTime;
  const riffleCount = 11;
  for (let i = 0; i < riffleCount; i++) {
    const offset = i * 0.042 + (i % 2) * 0.006;
    const pitch = 0.85 + (i / riffleCount) * 0.45;
    scheduleCardSnap(ctx, now + offset, pitch, 0.22);
  }
}

/** 2. Card placing sound when 1–5 cards are played onto the table. */
export function playCardPlaceSound(cardCount = 1) {
  if (isMutedGlobal()) return;
  const ctx = getAudioContext();
  if (!ctx) return;

  const now = ctx.currentTime;
  const snaps = Math.min(Math.max(1, cardCount), 3);
  for (let i = 0; i < snaps; i++) {
    scheduleCardSnap(ctx, now + i * 0.028, 1 + i * 0.1, 0.32);
  }

  // Subtle harmonic accent for 5-card combinations
  if (cardCount >= 5) {
    const freqs = [523.25, 659.25, 783.99];
    freqs.forEach((f, idx) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = 'triangle';
      const t0 = now + 0.04 + idx * 0.035;
      osc.frequency.setValueAtTime(f, t0);
      gain.gain.setValueAtTime(0.001, t0);
      gain.gain.linearRampToValueAtTime(0.08, t0 + 0.01);
      gain.gain.exponentialRampToValueAtTime(0.001, t0 + 0.18);
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.start(t0);
      osc.stop(t0 + 0.19);
    });
  }
}

/** Short resin-tile clack on felt, respecting the shared mute preference. */
export function playTilePlaceSound(tileCount = 1) {
  if (isMutedGlobal()) return;
  const ctx = getAudioContext();
  if (!ctx) return;
  const count = Math.min(10, Math.max(1, tileCount));
  for (let i = 0; i < count; i++) {
    const start = ctx.currentTime + i * .025;
    const noise = ctx.createBufferSource();
    noise.buffer = getNoiseBuffer(ctx);
    const filter = ctx.createBiquadFilter();
    filter.type = 'bandpass';
    filter.frequency.setValueAtTime(2600, start);
    filter.Q.setValueAtTime(.8, start);
    const click = ctx.createGain();
    click.gain.setValueAtTime(.001, start);
    click.gain.linearRampToValueAtTime(.22, start + .002);
    click.gain.exponentialRampToValueAtTime(.001, start + .032);
    noise.connect(filter); filter.connect(click); click.connect(ctx.destination);
    noise.start(start); noise.stop(start + .035);
    const body = ctx.createOscillator(), gain = ctx.createGain();
    body.type = 'triangle';
    body.frequency.setValueAtTime(520 - i * 8, start);
    body.frequency.exponentialRampToValueAtTime(190, start + .04);
    gain.gain.setValueAtTime(.16, start);
    gain.gain.exponentialRampToValueAtTime(.001, start + .05);
    body.connect(gain); gain.connect(ctx.destination);
    body.start(start); body.stop(start + .055);
  }
}

/** 3. Light paper flick when selecting/deselecting a card in hand. */
export function playCardSelectSound(selected: boolean) {
  if (isMutedGlobal()) return;
  const ctx = getAudioContext();
  if (!ctx) return;

  const now = ctx.currentTime;
  scheduleCardSnap(ctx, now, selected ? 1.35 : 0.95, 0.12);
}

/** 4. Soft double-tap table knock when a player passes. */
export function playPassSound() {
  if (isMutedGlobal()) return;
  const ctx = getAudioContext();
  if (!ctx) return;

  const now = ctx.currentTime;
  [0, 0.075].forEach((delay, idx) => {
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.type = 'sine';
    const t0 = now + delay;
    osc.frequency.setValueAtTime(idx === 0 ? 115 : 95, t0);
    osc.frequency.exponentialRampToValueAtTime(45, t0 + 0.055);
    gain.gain.setValueAtTime(0.16, t0);
    gain.gain.exponentialRampToValueAtTime(0.001, t0 + 0.06);
    osc.connect(gain);
    gain.connect(ctx.destination);
    osc.start(t0);
    osc.stop(t0 + 0.065);
  });
}

/** 5. Gentle chime when it becomes the local player's turn. */
export function playYourTurnSound() {
  if (isMutedGlobal()) return;
  const ctx = getAudioContext();
  if (!ctx) return;

  const now = ctx.currentTime;
  const notes = [523.25, 659.25]; // C5 -> E5
  notes.forEach((freq, idx) => {
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.type = 'triangle';
    const t0 = now + idx * 0.09;
    osc.frequency.setValueAtTime(freq, t0);
    gain.gain.setValueAtTime(0.001, t0);
    gain.gain.linearRampToValueAtTime(0.14, t0 + 0.012);
    gain.gain.exponentialRampToValueAtTime(0.001, t0 + 0.22);
    osc.connect(gain);
    gain.connect(ctx.destination);
    osc.start(t0);
    osc.stop(t0 + 0.24);
  });
}

/** 6. Urgent warning tick when turn timer becomes red (< 5 seconds left). */
export function playTimerRedTick(secondsRemaining: number) {
  if (isMutedGlobal()) return;
  const ctx = getAudioContext();
  if (!ctx) return;

  const now = ctx.currentTime;
  const isCritical = secondsRemaining <= 2;
  const baseFreq = isCritical ? 987.77 : 783.99; // B5 or G5

  const osc = ctx.createOscillator();
  const gain = ctx.createGain();
  osc.type = 'sine';
  osc.frequency.setValueAtTime(baseFreq, now);
  osc.frequency.exponentialRampToValueAtTime(baseFreq * 0.75, now + 0.045);

  gain.gain.setValueAtTime(0.001, now);
  gain.gain.linearRampToValueAtTime(isCritical ? 0.18 : 0.12, now + 0.005);
  gain.gain.exponentialRampToValueAtTime(0.001, now + 0.055);

  osc.connect(gain);
  gain.connect(ctx.destination);
  osc.start(now);
  osc.stop(now + 0.06);
}

/** 7. End of round victory or completion sound. */
export function playGameOverSound(didWin: boolean) {
  if (isMutedGlobal()) return;
  const ctx = getAudioContext();
  if (!ctx) return;

  const now = ctx.currentTime;
  const notes = didWin ? [523.25, 659.25, 783.99, 1046.5] : [392.0, 329.63];
  notes.forEach((freq, idx) => {
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.type = 'triangle';
    const t0 = now + idx * 0.1;
    osc.frequency.setValueAtTime(freq, t0);
    gain.gain.setValueAtTime(0.001, t0);
    gain.gain.linearRampToValueAtTime(0.15, t0 + 0.015);
    gain.gain.exponentialRampToValueAtTime(0.001, t0 + 0.28);
    osc.connect(gain);
    gain.connect(ctx.destination);
    osc.start(t0);
    osc.stop(t0 + 0.3);
  });
}

/** 8. High-impact countdown audio ticks for match start loading screen. */
export function playMatchCountdownTick(isFinal = false) {
  if (isMutedGlobal()) return;
  const ctx = getAudioContext();
  if (!ctx) return;
  try {
    const now = ctx.currentTime;
    if (isFinal) {
      // Energetic celebratory chord for GO!
      const chordNotes = [523.25, 659.25, 783.99, 1046.5]; // C5, E5, G5, C6
      chordNotes.forEach((freq, idx) => {
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.type = 'triangle';
        const t0 = now + idx * 0.02;
        osc.frequency.setValueAtTime(freq, t0);
        gain.gain.setValueAtTime(0.001, t0);
        gain.gain.linearRampToValueAtTime(0.14, t0 + 0.015);
        gain.gain.exponentialRampToValueAtTime(0.001, t0 + 0.32);
        osc.connect(gain);
        gain.connect(ctx.destination);
        osc.start(t0);
        osc.stop(t0 + 0.35);
      });
    } else {
      // Crisp retro-arcade / esports countdown beep (sine ping)
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = 'sine';
      osc.frequency.setValueAtTime(880, now); // A5
      gain.gain.setValueAtTime(0.001, now);
      gain.gain.linearRampToValueAtTime(0.16, now + 0.008);
      gain.gain.exponentialRampToValueAtTime(0.001, now + 0.09);
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.start(now);
      osc.stop(now + 0.1);
    }
  } catch {}
}

let lastMatchFoundAt = 0;

export function _resetMatchFoundForTest() {
  lastMatchFoundAt = 0;
}

/** 9. Uplifting, punchy celebratory chime when a multiplayer match is found. */
export function playMatchFoundSound() {
  if (isMutedGlobal()) return;
  const nowMs = Date.now();
  if (nowMs - lastMatchFoundAt < 1200) return;
  lastMatchFoundAt = nowMs;

  const ctx = getAudioContext();
  if (!ctx) return;

  try {
    const now = ctx.currentTime;

    // 1. Warm resonant body (D4 bell fundamental)
    const bodyOsc = ctx.createOscillator();
    const bodyGain = ctx.createGain();
    bodyOsc.type = 'sine';
    bodyOsc.frequency.setValueAtTime(293.66, now);
    bodyOsc.frequency.exponentialRampToValueAtTime(146.83, now + 0.4);

    bodyGain.gain.setValueAtTime(0.001, now);
    bodyGain.gain.linearRampToValueAtTime(0.18, now + 0.008);
    bodyGain.gain.exponentialRampToValueAtTime(0.001, now + 0.45);

    bodyOsc.connect(bodyGain);
    bodyGain.connect(ctx.destination);
    bodyOsc.start(now);
    bodyOsc.stop(now + 0.46);

    // 2. Ascending 4-tone triumphant arpeggio: D5 -> G5 -> B5 -> D6
    const arpeggio = [
      { freq: 587.33, delay: 0.0, dur: 0.32 },
      { freq: 783.99, delay: 0.07, dur: 0.35 },
      { freq: 987.77, delay: 0.14, dur: 0.42 },
      { freq: 1174.66, delay: 0.21, dur: 0.60 },
    ];

    arpeggio.forEach(({ freq, delay, dur }, idx) => {
      const isPeak = idx === arpeggio.length - 1;
      const t0 = now + delay;
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();

      osc.type = isPeak ? 'sine' : 'triangle';
      osc.frequency.setValueAtTime(freq, t0);

      const maxGain = isPeak ? 0.22 : 0.14;
      gain.gain.setValueAtTime(0.001, t0);
      gain.gain.linearRampToValueAtTime(maxGain, t0 + 0.012);
      gain.gain.exponentialRampToValueAtTime(0.001, t0 + dur);

      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.start(t0);
      osc.stop(t0 + dur + 0.02);

      // Shimmer overtone on the final peak note for esports brilliance
      if (isPeak) {
        const shimmer = ctx.createOscillator();
        const shimmerGain = ctx.createGain();
        shimmer.type = 'triangle';
        shimmer.frequency.setValueAtTime(freq * 1.5, t0); // Fifth (A6, 1762 Hz)
        shimmerGain.gain.setValueAtTime(0.001, t0);
        shimmerGain.gain.linearRampToValueAtTime(0.09, t0 + 0.018);
        shimmerGain.gain.exponentialRampToValueAtTime(0.001, t0 + 0.52);

        shimmer.connect(shimmerGain);
        shimmerGain.connect(ctx.destination);
        shimmer.start(t0);
        shimmer.stop(t0 + 0.55);
      }
    });
  } catch {}
}

interface SoundContextValue {
  muted: boolean;
  toggleMute: () => void;
}

const SoundContext = createContext<SoundContextValue>({
  muted: false,
  toggleMute: () => {},
});

export function SoundProvider({ children }: { children: React.ReactNode }) {
  const [muted, setMuted] = useState<boolean>(getInitialMuted);

  const toggleMute = () => {
    setMuted((prev) => {
      const next = !prev;
      if (typeof localStorage !== 'undefined' && typeof localStorage?.setItem === 'function') {
        localStorage.setItem(MUTE_STORAGE_KEY, String(next));
      }
      if (!next) {
        playCardSelectSound(true);
      }
      return next;
    });
  };

  return <SoundContext.Provider value={{ muted, toggleMute }}>{children}</SoundContext.Provider>;
}

export function useSound() {
  return useContext(SoundContext);
}
